import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import '@ant-design/compatible/assets/index.css';
import { Alert, Select, InputNumber, Form, Button, message, Switch, Input, Drawer } from 'antd';
import { FormComponentProps } from '@ant-design/compatible/lib/form/Form';
import { connect } from 'react-redux';
import {
	getCurrency,
	isFiniteNumber,
	nullSafeGet,
	roundOffToDecimal,
} from '../../utils/DataAccessUtils';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { partsRestCrudThunksForSupplier } from '../../thunks/parts_thunks';
import { partCatalogsRestCrudThunksForSupplier } from '../../thunks/part_catalogs_thunks';
import { useForm } from 'antd/lib/form/Form';
import { partsPerStockLocationsRestCrudThunksForSupplier } from '../../thunks/parts_per_stock_locations_thunks';
import { withRouter } from 'react-router';
import BackButton from '../back_button/BackButton';
import PartWithIDDisplay from '../parts_detail_details_page/PartWithIDDisplay';
import PartForm from '../part_form/PartForm';
import { PERMISSION_NAMES, isSupplierAllowedToAccess } from '../../utils/AuthUtils';
import PPSLAdjustmentModal from '../stock_locations_detail_details_page/PPSLAdjustmentModal';
import PartEquipmentPricePicker from '../common/PartEquipmentPricePicker';

interface StockLocationAddPartFormProps extends FormComponentProps {
	onCancel: any;
	visible: boolean;
	createErrors: any;
	updateErrors: any;
	creating: boolean;
	updating: boolean;
	formData: any;

	fetchParts: any;
	fetchMultipleParts: any;
	fetchPartsCatalog: any;
	parts: any;

	stockLocationId: any;

	createPart: any;
	onSuccess: any;
	match: any;
	companyConfig: any;
	currentUser: any;
}
require('./StockLocationAddOrEditPartForm.less');

const StockLocationAddOrEditPartForm: FC<StockLocationAddPartFormProps> = ({
	createErrors,
	updateErrors,
	parts,
	fetchParts,
	creating,
	fetchMultipleParts,
	fetchPartsCatalog,
	createPart,
	onSuccess,
	stockLocationId,
	formData,
	companyConfig,
	currentUser,
}): React.ReactElement => {
	const [form] = useForm();

	const { setFieldsValue } = form;

	const [showCreatePart, setShowCreatePart] = useState(false);
	const [newPartName, setNewPartName] = useState('');
	const [adjustModalVisible, setAdjustModalVisible] = useState(false);

	const isEdit = useMemo(() => nullSafeGet('id', formData), [formData]);

	const userCurrencyId = useMemo(
		() => nullSafeGet('id', getCurrency({ currentUser })),
		[currentUser]
	);

	const initialValues = useMemo(() => formData, [formData]);

	const watchedPartId = Form.useWatch('partId', form);
	const watchedOnHandQuantity = Form.useWatch('onHandQuantity', form);
	const watchedTotalStockedPartsCost = Form.useWatch('totalStockedPartsCost', form);

	const watchedPartRecord = useMemo(() => {
		const partsRecords = nullSafeGet('records', parts);
		return partsRecords ? partsRecords[watchedPartId] : null;
	}, [parts, watchedPartId]);

	const partDefaultVendorId = useMemo(
		() => nullSafeGet('defaultVendorId', watchedPartRecord),
		[watchedPartRecord]
	);

	const partCost = useMemo(() => nullSafeGet('cost', watchedPartRecord), [watchedPartRecord]);
	const hasPartCost = partCost !== null && partCost !== undefined && partCost !== '';

	const [selectedCost, setSelectedCost] = useState(null);
	const [catalogCurrencyId, setCatalogCurrencyId] = useState(userCurrencyId);

	// Show the picker when there is a vendor to fetch a catalog from OR a part
	// cost to fall back to (a part can have a cost but no default vendor).
	const showPricePicker = !isEdit && watchedPartId && (partDefaultVendorId || hasPartCost);

	const qtyIsNumber = isFiniteNumber(watchedOnHandQuantity);
	const costIsNumber = isFiniteNumber(watchedTotalStockedPartsCost);

	const onPriceSelect = useCallback(
		(unitPrice) => {
			setSelectedCost(unitPrice);
			const quantityValue = form.getFieldValue('onHandQuantity');
			if (isFiniteNumber(quantityValue)) {
				setFieldsValue({
					totalStockedPartsCost: roundOffToDecimal(unitPrice * quantityValue),
				});
			}
		},
		[form, setFieldsValue]
	);

	const onCatalogLoad = useCallback(
		(_catalog, nextCatalogCurrencyId) => {
			setCatalogCurrencyId(nextCatalogCurrencyId || userCurrencyId);
		},
		[userCurrencyId]
	);

	// Clear picker selection when part changes — the new part's catalog is a
	// different price source.
	useEffect(() => {
		setSelectedCost(null);
	}, [watchedPartId]);

	// Keep totalStockedPartsCost in sync with selectedCost * onHandQuantity once
	// a price is picked and a quantity is entered.
	useEffect(() => {
		if (selectedCost === null || selectedCost === undefined) return;
		if (!qtyIsNumber) return;
		const projectedTotal = roundOffToDecimal(selectedCost * watchedOnHandQuantity);
		if (projectedTotal !== roundOffToDecimal(watchedTotalStockedPartsCost)) {
			setFieldsValue({ totalStockedPartsCost: projectedTotal });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [watchedOnHandQuantity, selectedCost]);

	// Clear the picker selection when the user manually edits total cost away
	// from selectedCost * onHandQuantity.
	useEffect(() => {
		if (selectedCost === null || selectedCost === undefined) return;
		if (!qtyIsNumber || !costIsNumber) return;
		const projectedTotal = roundOffToDecimal(selectedCost * watchedOnHandQuantity);
		if (projectedTotal !== roundOffToDecimal(watchedTotalStockedPartsCost)) {
			setSelectedCost(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [watchedTotalStockedPartsCost]);

	const handleAddPartSubmit = useCallback(
		(values) => {
			const partsPerStockLocation = {
				...(formData || {}),
				...values,
				isStocked: !!values.isStocked,
				stockLocationId: parseInt(stockLocationId),
				totalStockedPartsCost: parseFloat(values.totalStockedPartsCost || 0),
			};

			createPart(partsPerStockLocation).then(() => {
				onSuccess();
				message.success(isEdit ? 'Updated successfully!' : `Added a new part.`);
			});
		},
		[createPart, formData, isEdit, onSuccess, stockLocationId]
	);

	const hideCreatePart = useCallback(() => {
		setShowCreatePart(false);
		setNewPartName('');
	}, []);

	const onCreatePart = useCallback((val) => {
		return new Promise((resolve) => {
			setShowCreatePart(true);
			setNewPartName(val);
			resolve(null);
		});
	}, []);

	const onCreatePartSuccess = useCallback(
		(record) => {
			message.success('Part created successfully!');
			nullSafeGet('id', record) &&
				setFieldsValue({
					partId: record.id,
				});
			hideCreatePart();
		},
		[hideCreatePart, setFieldsValue]
	);

	const canCreatePartOrEquipment = useMemo(
		() =>
			isSupplierAllowedToAccess(
				PERMISSION_NAMES.MODIFY_PARTS_AND_EQUIPMENTS,
				companyConfig,
				currentUser.roles
			),
		[companyConfig, currentUser.roles]
	);

	const _showAdjustModal = () => setAdjustModalVisible(true);
	const _hideAdjustModal = () => setAdjustModalVisible(false);

	const handleAdjustSuccess = useCallback(
		(values) => {
			_hideAdjustModal();
			const { availableQuantity, onHandQuantity, totalStockedPartsCost } = values;
			setFieldsValue({ onHandQuantity, availableQuantity, totalStockedPartsCost });
		},
		[setFieldsValue]
	);

	return (
		<Form
			form={form}
			style={{ width: 600 }}
			id="add-part-form"
			layout="vertical"
			requiredMark={false}
			initialValues={initialValues}
			onFinish={handleAddPartSubmit}
		>
			{createErrors.length > 0 ? (
				<Form.Item>
					<Alert message={createErrors.join(' ')} type="error" />
				</Form.Item>
			) : null}
			{updateErrors.length > 0 ? (
				<Form.Item>
					<Alert message={updateErrors.join(' ')} type="error" />
				</Form.Item>
			) : null}
			<Form.Item
				label="Part"
				name="partId"
				rules={[{ required: true, message: 'This field is required.' }]}
			>
				<OWAsyncSelect
					selectProps={{
						className: 'ow-async-select--autoheight',
					}}
					resetSearchWhenOpenSelect
					disabled={isEdit}
					stateSlice={parts}
					targetCollectionName="stockLocationAssociatedParts"
					fetchMultiple={(ids, targetCollectionName) => {
						fetchMultipleParts(ids, targetCollectionName);
					}}
					fetchData={(
						search,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
					) => {
						fetchParts(
							{ search },
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						);
					}}
					renderRecord={(part) => {
						return (
							<Select.Option key={part.id} value={part.id}>
								<PartWithIDDisplay part={part} numberMaxChars={80} />
							</Select.Option>
						);
					}}
					createNewEntity={canCreatePartOrEquipment ? onCreatePart : undefined}
					newEntityLabelChecker={(part) => part.name}
					labelAccessor={(part) => <PartWithIDDisplay part={part} />}
					sortBy={{ sort_by: 'name', order: 'ascend' }}
				/>
			</Form.Item>
			<Form.Item name="isStocked" label="Is Stocked" valuePropName="checked" initialValue={'true'}>
				<Switch />
			</Form.Item>
			<Form.Item
				name="onHandQuantity"
				label="On Hand Qty"
				help={
					isEdit ? (
						<div className="flex flex-row items-baseline">
							<Button type="link" onClick={_showAdjustModal}>
								Click here
							</Button>
							<div>to change the on hand qty</div>
						</div>
					) : (
						''
					)
				}
			>
				<InputNumber style={{ width: '100%' }} disabled={isEdit} />
			</Form.Item>
			<Form.Item
				name="totalStockedPartsCost"
				label={
					<div className="flex flex-row items-center">
						Total Cost
						{showPricePicker ? (
							<PartEquipmentPricePicker
								entityId={watchedPartId}
								entityName="partId"
								vendorId={partDefaultVendorId}
								fetchMethod={fetchPartsCatalog}
								fallbackCurrencyId={userCurrencyId}
								value={selectedCost}
								onChange={onPriceSelect}
								onCatalogLoad={onCatalogLoad}
								entityCost={partCost}
								defaultToListPrice
							/>
						) : null}
					</div>
				}
				help={
					isEdit ? (
						<div className="flex flex-row items-baseline">
							<Button type="link" onClick={_showAdjustModal}>
								Click here
							</Button>
							<div>to change the total cost</div>
						</div>
					) : (
						''
					)
				}
			>
				<InputNumber
					style={{ width: '100%' }}
					disabled={isEdit}
					addonAfter={isEdit ? userCurrencyId : catalogCurrencyId}
				/>
			</Form.Item>
			{isEdit && (
				<Form.Item name="availableQuantity" label="Available Qty">
					<InputNumber style={{ width: '100%' }} disabled />
				</Form.Item>
			)}
			<Form.Item name="minQuantity" label="Min Qty">
				<InputNumber style={{ width: '100%' }} />
			</Form.Item>
			<Form.Item name="maxQuantity" label="Max Qty">
				<InputNumber style={{ width: '100%' }} />
			</Form.Item>
			<Form.Item name="aisle" label="Aisle">
				<Input style={{ width: '100%' }} />
			</Form.Item>
			<Form.Item name="bay" label="Bay">
				<Input style={{ width: '100%' }} />
			</Form.Item>
			<Form.Item name="level" label="Level">
				<Input style={{ width: '100%' }} />
			</Form.Item>
			<Form.Item name="bin" label="Bin">
				<Input />
			</Form.Item>
			<Form.Item>
				<BackButton buttonText="Cancel" />
				<Button
					type="primary"
					size="large"
					style={{ marginLeft: '16px' }}
					key="submit"
					htmlType="submit"
					form="add-part-form"
					loading={creating}
				>
					{`${isEdit ? 'Update' : `Add`} Part`}
				</Button>
			</Form.Item>
			<Drawer
				width="50%"
				placement="right"
				closable={true}
				onClose={hideCreatePart}
				visible={showCreatePart}
			>
				<PartForm
					formData={{
						name: newPartName,
					}}
					onSuccess={onCreatePartSuccess}
				/>
			</Drawer>
			{adjustModalVisible ? (
				<PPSLAdjustmentModal
					ppsl={formData}
					onCancel={_hideAdjustModal}
					onSuccess={handleAdjustSuccess}
				/>
			) : null}
		</Form>
	);
};

const mapStateToProps = (state, ownProps) => ({
	visible: ownProps.visible,
	onCancel: ownProps.onCancel,
	onSubmit: ownProps.onSubmit,
	createErrors: state.parts_per_stock_locations.createErrors,
	updateErrors: state.parts_per_stock_locations.updateErrors,
	creating: state.parts_per_stock_locations.creating,
	updating: state.parts_per_stock_locations.updating,

	parts: state.parts,
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	fetchParts: (params, targetCollectionName, pagination, sorting, filters, addToTargetCollection) =>
		dispatch(
			partsRestCrudThunksForSupplier.read(
				{ ...(params || {}), isActive: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleParts: (ids, targetCollectionName) =>
		dispatch(partsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchPartsCatalog: (params, targetCollectionName) =>
		dispatch(partCatalogsRestCrudThunksForSupplier.read(params, targetCollectionName)),
	createPart: (entity) => dispatch(partsPerStockLocationsRestCrudThunksForSupplier.create(entity)),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(StockLocationAddOrEditPartForm)
);
